import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';

const STATUS = { ACTIVE: ['green', 'Actif'], TERMINATED: ['red', 'Terminé'], SUSPENDED: ['orange', 'Suspendu'], EXPIRED: ['gray', 'Expiré'] };

export default function Contrats() {
  const [tab, setTab] = useState('liste');
  return (
    <>
      <div className="tabs" style={{ marginBottom: 4 }}>
        <button className={`tab ${tab === 'liste' ? 'active' : ''}`} onClick={() => setTab('liste')}>Contrats actifs</button>
        <button className={`tab ${tab === 'archive' ? 'active' : ''}`} onClick={() => setTab('archive')}>Historique</button>
      </div>
      {tab === 'liste' && <ListeContrats />}
      {tab === 'archive' && <ArchiveContrats />}
    </>
  );
}

function ListeContrats() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);

  function load() {
    api.get('/hr/contracts?status=ACTIVE').then(setRows).catch((e) => setError(e.message));
  }

  useEffect(load, []);

  async function archive(id) {
    if (!(await confirmDialog({ title: 'Archiver', message: 'Archiver ce contrat ?', confirmLabel: 'Archiver', danger: true }))) return;
    try { await api.post(`/hr/contracts/${id}/archive`); load(); } catch (e) { setError(e.message); }
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>Contrats du personnel</h2>
        <button className="btn btn-sm" onClick={() => setModal({})}>+ Nouveau contrat</button>
      </div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucun contrat actif.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Agent</th><th>Poste</th><th>Type</th><th>Début</th><th>Fin</th><th>Salaire</th><th>Statut</th><th></th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id}>
                <td><b>{r.lastName} {r.firstName}</b><br /><small className="mono">{r.matricule}</small></td>
                <td>{r.position}</td>
                <td><small>{r.typeName}</small></td>
                <td className="mono">{new Date(r.startDate).toLocaleDateString('fr-FR')}</td>
                <td className="mono">{r.endDate ? new Date(r.endDate).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="num">{Number(r.baseSalary).toLocaleString('fr-FR')} XOF</td>
                <td><span className={`badge ${STATUS[r.status][0]}`}>{STATUS[r.status][1]}</span></td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn-ghost btn-xs" onClick={() => setModal(r)}>Modifier</button>
                  <button className="link-danger" style={{ fontSize: '0.75rem' }} onClick={() => archive(r.id)}>Archiver</button>
                </td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {modal && <ContratModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function ArchiveContrats() {
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/hr/contracts?status=ARCHIVED').then(setRows).catch((e) => setError(e.message));
  }, []);

  return (
    <div className="panel">
      <div className="panel-head"><h2>Contrats archivés</h2></div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucun contrat archivé.</div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Agent</th><th>Poste</th><th>Début</th><th>Fin</th><th>Salaire</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id}>
                <td><b>{r.lastName} {r.firstName}</b></td>
                <td>{r.position}</td>
                <td className="mono">{new Date(r.startDate).toLocaleDateString('fr-FR')}</td>
                <td className="mono">{r.endDate ? new Date(r.endDate).toLocaleDateString('fr-FR') : '—'}</td>
                <td className="num">{Number(r.baseSalary).toLocaleString('fr-FR')} XOF</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}

function ContratModal({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState(isNew
    ? { staffId: '', contractTypeId: '', positionTypeId: '', startDate: '', baseSalary: '', endDate: '' }
    : { position: initial.position, baseSalary: initial.baseSalary, endDate: initial.endDate ? initial.endDate.slice(0, 10) : '' }
  );
  const [staffs, setStaffs] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [positionTypes, setPositionTypes] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) {
      api.get('/staff').then(setStaffs).catch((e) => console.error('staff:', e));
      api.get('/hr/contract-types').then(setContractTypes).catch((e) => console.error('contract-types:', e));
      api.get('/hr/position-types').then(setPositionTypes).catch((e) => console.error('position-types:', e));
    }
  }, [isNew]);

  async function save() {
    if (isNew && (!form.staffId || !form.contractTypeId || !form.positionTypeId || !form.startDate || !form.baseSalary)) {
      setError('Tous les champs sont requis.');
      return;
    }
    if (form.endDate && form.endDate <= form.startDate) {
      setError('La date de fin doit être postérieure à la date de début.');
      return;
    }
    setSaving(true);
    try {
      const payload = isNew ? { ...form, position: form.positionTypeId } : form;
      if (isNew) {
        await api.post('/hr/contracts', payload);
      } else {
        await api.patch(`/hr/contracts/${initial.id}`, payload);
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isNew ? 'Nouveau contrat' : 'Modifier le contrat'}</h2>
        {error && <div className="form-error">{error}</div>}

        {isNew && (
          <>
            <div className="field">
              <label>Agent</label>
              <select value={form.staffId} onChange={(e) => setForm({ ...form, staffId: e.target.value })}>
                <option value="">Sélectionner…</option>
                {staffs.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Type de contrat</label>
              <select value={form.contractTypeId} onChange={(e) => setForm({ ...form, contractTypeId: e.target.value })}>
                <option value="">Sélectionner…</option>
                {contractTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Poste</label>
              <select value={form.positionTypeId} onChange={(e) => setForm({ ...form, positionTypeId: e.target.value })}>
                <option value="">Sélectionner…</option>
                {positionTypes.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Date de début</label>
              <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
            </div>
          </>
        )}

        <div className="field">
          <label>Salaire (XOF)</label>
          <input type="number" value={form.baseSalary || ''} onChange={(e) => setForm({ ...form, baseSalary: Number(e.target.value) })} />
        </div>
        <div className="field">
          <label>Date de fin</label>
          <input type="date" value={form.endDate || ''} onChange={(e) => setForm({ ...form, endDate: e.target.value })} />
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
          <button className="btn" onClick={save} disabled={saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
        </div>
      </div>
    </div>
  );
}
